import React, {useEffect, useState} from 'react';
import PropTypes from "prop-types";
import Icon from 'SF/MaterialIcon';
import css from './Dashboard.css';
import Widget from "./Widget";
import { useWindowSize } from "react-use";
import Announcement from '../systemAnnouncement/Announcement';
import ErrorMessage from "../common/ErrorMessage";
import RequestHelpModal from "./RequestHelpModal";
import Toast from "../common/Toast";
import { getWord } from '../utils/tenant';
import {RefreshProvider} from "./RefreshProvider";

const Dashboard = (props) => {
  const { width: screenWidth } = useWindowSize();
  const [openRequestHelpModal, setOpenRequestHelpModal] = useState(false)
  const [error, setError] = useState(null)
  const [toastMessage, setToastMessage] = useState("")

  // If one of the widgets has been marked with a bookmark reference, scroll to it
  // upon the page load finishing.
  useEffect(() => {
    if (props.scrollRef.current) {
      setTimeout(() => { props.scrollRef.current.scrollIntoView() }, 30)
    }
  }, []);

  const handleRequestHelpClick = () => {
    setOpenRequestHelpModal(true);
  }

  const showWidget = (widgetType) => {
    switch (widgetType) {
      case 'calendar': return props.showCalendarWidget;
      case 'notifications': return props.showNotificationWidget;
      case 'connection': return props.showConnectionWidget;
      case 'services': return props.showServiceWidget;
      case 'kudos': return true;
    }
  }

  const closeModal = () => { setOpenRequestHelpModal(false)}
  const successRequestHelp = () => { setToastMessage("Thank you for submitting your request.") }
  const successToastMessage = (message) => { setToastMessage(message) }

  // Add a ref if the URL includes a hash matching one of the widget bookmark identifiers
  const findBookmarkRef = (widgetBookmark) =>
      window.location.hash.slice(2) === widgetBookmark ? props.scrollRef : null

  const widgets = [
    {'title': 'Calendar', 'icon': 'event_note', 'type': 'calendar', 'bookmark': 'widget_a'},
    {'title': 'Notifications', 'icon': 'flag', 'type': 'notifications', 'bookmark': 'widget_b'},
    {'title': 'My Connections', 'icon': 'person', 'type': 'connection', 'bookmark': 'widget_c'},
    {'title': 'My Services', 'icon': 'account_balance', 'type': 'services', 'bookmark': 'widget_d'},
    {'title': getWord('kudos'), 'icon': 'star', 'type': 'kudos', 'bookmark': null}
  ];

  return (
    <div className={css.container}>
      <RefreshProvider>
      {error && <ErrorMessage message={error}/>}
      <Announcement studentAnnouncementEnabled={props.studentAnnouncementEnabled} />
      <div className={css.banner}>
        <Icon aria-hidden="true" icon="dashboard"/>
        <h1>Dashboard</h1>
      </div>
      {props.studentHelpEnabled && (!props.studentHelpContentEmpty || props.studentSelfFlaggingEnabled) &&
        <div className={css['request-help']}>
          <button onClick={handleRequestHelpClick} className={css['request-help-button']}>
            <Icon icon='pan_tool' aria-hidden='true' className={css["request-help-icon"]}/>
            { props.studentHelpText }
          </button>
        </div>
      }
      <div className={css.main} data-role="widgetsContainer">
        {widgets.
          filter((widget) => showWidget(widget.type)).
          map((widget, index) => (
          <Widget key={index}
                  screenWidth={screenWidth}
                  scrollRef={findBookmarkRef(widget.bookmark)}
                  {...widget}
                  setError={setError}
                  studentId={props.studentId}
                  successNetworkText={props.successNetworkText}
                  successToastMessage={successToastMessage}
                  hideServices={props.hideServices}
                  responsiveKioskEnabled={props.responsiveKioskEnabled}
                  virtualCheckInEnabled={props.virtualCheckInEnabled}
                  requestHelp={{
                    studentHelpText: props.studentHelpText,
                    requestHelpEnabled: props.requestHelpEnabled
                  }}/>
        ))}
      </div>
      <RequestHelpModal openRequestHelpModal={openRequestHelpModal}
                        studentId={props.studentId}
                        closeModal={closeModal}
                        cancelButtonText={'Cancel'}
                        successRequestHelp={successRequestHelp}
                        studentSelfFlaggingEnabled={props.studentSelfFlaggingEnabled}
                        requestHelpEnabled={props.requestHelpEnabled} />
      {toastMessage && <Toast message={toastMessage} onTimeout={() => setToastMessage("")} />}
      </RefreshProvider>
    </div>
  );
};

Dashboard.propTypes = {
  studentId: PropTypes.number.isRequired,
  studentAnnouncementEnabled: PropTypes.bool.isRequired,
  hideServices: PropTypes.bool,
  scrollRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ]),
  showCalendarWidget: PropTypes.bool.isRequired,
  showNotificationWidget: PropTypes.bool.isRequired,
  showConnectionWidget: PropTypes.bool.isRequired,
  showServiceWidget: PropTypes.bool.isRequired,
  studentHelpContentEmpty: PropTypes.bool,
  studentHelpEnabled: PropTypes.bool,
  studentHelpText: PropTypes.string.isRequired,
  studentSelfFlaggingEnabled: PropTypes.bool,
  requestHelpEnabled: PropTypes.bool,
  successNetworkText: PropTypes.string.isRequired,
  responsiveKioskEnabled: PropTypes.bool,
  virtualCheckInEnabled: PropTypes.bool,
};

export default Dashboard;
